import React from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '@/components/SEOHead';
import { MessageSquare, Image, Receipt, Truck, BellRing, ShieldCheck } from 'lucide-react';
import { MarketingShell, PageHero, PageSection, CardGrid, FaqList, ClosingCta, buildFaqJsonLd, buildBreadcrumb } from '@/components/marketing/MarketingShell';

const FAQ = [
  {
    q: 'Apakah Nerava bisa auto reply WhatsApp?',
    a: 'Bisa. Setiap chat yang masuk ke nomor WhatsApp bisnis Anda dijawab otomatis oleh AI Nerava memakai data katalog produk, stok, dan ongkir Anda, 24 jam sehari.',
  },
  {
    q: 'Nomor WhatsApp saya aman atau bisa kena blokir?',
    a: 'Nerava memakai WhatsApp Business API resmi dari Meta, bukan aplikasi modifikasi. Selama isi pesan mengikuti kebijakan Meta, nomor Anda tidak berisiko diblokir seperti pada tools tidak resmi.',
  },
  {
    q: 'Apakah AI bisa memproses pesanan sampai selesai?',
    a: 'Ya. AI bisa menanyakan varian, menghitung ongkir, mengirim rincian pesanan dan nomor rekening, lalu membaca bukti transfer yang dikirim pembeli dan mencocokkan nominalnya.',
  },
  {
    q: 'Bisakah saya ambil alih chat kapan saja?',
    a: 'Bisa. Semua chat tampil di inbox Nerava. Begitu Anda membalas manual, AI berhenti di percakapan itu sampai Anda serahkan kembali.',
  },
  {
    q: 'Apakah bisa kirim foto produk otomatis?',
    a: 'Bisa. Kalau pembeli minta foto, AI mengirim foto produk dari katalog Anda lengkap dengan harga dan varian yang tersedia.',
  },
];

const JSON_LD = [buildBreadcrumb('AI WhatsApp', '/ai-whatsapp'), buildFaqJsonLd(FAQ)];

const FEATURES = [
  { icon: <MessageSquare className="w-5 h-5" />, title: 'Auto reply 24 jam', description: 'Pertanyaan harga, stok, dan ketersediaan varian dijawab langsung, termasuk di luar jam kerja dan hari libur.' },
  { icon: <Image className="w-5 h-5" />, title: 'Kirim foto produk', description: 'Pembeli minta "fotonya dong" dan AI langsung mengirim foto dari katalog beserta harga dan pilihan varian.' },
  { icon: <Truck className="w-5 h-5" />, title: 'Hitung ongkir dari chat', description: 'Cukup sebut kota tujuan, ongkir dihitung dan ditambahkan ke rincian pesanan tanpa Anda buka kalkulator.' },
  { icon: <Receipt className="w-5 h-5" />, title: 'Baca bukti transfer', description: 'Foto bukti transfer dibaca otomatis, nominalnya dicocokkan dengan tagihan, lalu status pesanan diperbarui.' },
  { icon: <BellRing className="w-5 h-5" />, title: 'Follow up pembeli diam', description: 'Calon pembeli yang berhenti di tengah jalan diingatkan kembali dengan pesan yang Anda atur sendiri.' },
  { icon: <ShieldCheck className="w-5 h-5" />, title: 'API resmi Meta', description: 'Terhubung lewat WhatsApp Business API resmi, jadi riwayat chat dan nomor bisnis Anda tetap aman.' },
];

const AiWhatsAppPage = () => (
  <MarketingShell active="/ai-whatsapp">
    <SEOHead
      title="Auto Reply WhatsApp dengan AI: Chat Jadi Order | Nerava"
      description="AI Nerava membalas chat WhatsApp 24 jam: jawab harga dan stok, kirim foto produk, hitung ongkir, sampai baca bukti transfer. Pakai WhatsApp Business API resmi. Coba gratis 30 hari."
      canonical="https://nerava.id/ai-whatsapp"
      jsonLd={JSON_LD}
    />
    <PageHero
      eyebrow="AI WhatsApp"
      title="Chat WhatsApp dibalas cepat"
      highlight="sampai jadi pesanan"
      subtitle="Dari pertanyaan harga sampai bukti transfer masuk, AI Nerava mengurus alurnya di WhatsApp. Anda tinggal menyiapkan barangnya."
      bullets={['WhatsApp Business API resmi', 'Balas, kirim foto, hitung ongkir', 'Bukti transfer dibaca otomatis']}
    />

    <PageSection title="Yang dikerjakan AI di WhatsApp Anda" intro="Bukan sekadar balasan template. Nerava menjalankan alur jualan yang biasanya Anda ketik manual satu per satu." tinted>
      <CardGrid items={FEATURES} />
    </PageSection>

    <PageSection title="Contoh alur yang sering terjadi">
      <div className="max-w-3xl space-y-3">
        {[
          ['Pembeli', 'Kak, sofa minimalis yang abu-abu masih ada?'],
          ['Nerava AI', 'Masih ada, Kak. Sofa Minimalis 2 Dudukan warna abu-abu Rp 3.450.000, stok 4. Saya kirim fotonya ya.'],
          ['Pembeli', 'Ongkir ke Bandung berapa?'],
          ['Nerava AI', 'Ongkir ke Bandung Rp 185.000. Total jadi Rp 3.635.000. Kalau cocok, saya buatkan pesanannya sekarang.'],
          ['Pembeli', '(mengirim foto bukti transfer)'],
          ['Nerava AI', 'Transfer Rp 3.635.000 sudah kami terima dan cocok dengan tagihan. Pesanan masuk antrean pengiriman, nomor resi menyusul.'],
        ].map(([who, text], i) => (
          <div
            key={i}
            className={`rounded-2xl px-5 py-4 text-sm leading-relaxed ${
              who === 'Pembeli'
                ? 'bg-slate-100 text-slate-700 dark:bg-zinc-900 dark:text-zinc-300'
                : 'bg-indigo-600 text-white ml-auto max-w-[90%]'
            }`}
          >
            <span className="block text-xs font-semibold uppercase tracking-[0.14em] opacity-70 mb-1">{who}</span>
            {text}
          </div>
        ))}
      </div>
      <p className="mt-8 text-sm text-slate-500 dark:text-zinc-400">
        Pesanan dari percakapan ini otomatis masuk ke{' '}
        <Link to="/crm-pesanan" className="text-indigo-600 dark:text-indigo-400 hover:underline" data-testid="link-to-crm-from-wa">
          CRM & manajemen pesanan
        </Link>{' '}
        lengkap dengan data pembeli dan statusnya.
      </p>
    </PageSection>

    <PageSection title="Pertanyaan soal WhatsApp" tinted>
      <FaqList items={FAQ} />
    </PageSection>

    <ClosingCta
      title="Hubungkan WhatsApp Anda hari ini"
      subtitle="Setup sekitar 15 menit. Setelah itu setiap chat yang masuk punya penjawab, bahkan saat Anda sedang tidak pegang HP."
    />
  </MarketingShell>
);

export default AiWhatsAppPage;
